package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initOpds_hostile_inputPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("opds_hostile_input.md", FrontMatterElement.Builder { addScalar("title", "Every catalogue is hostile: an OPDS client in Kotlin Multiplatform"); addScalar("description", "Building Parrot's OPDS catalogue browser in shared Kotlin: parsing two feed formats, keeping passwords on their origin, and the nine bugs a security review found."); addScalar("date", "2026-10-13"); addScalar("cover", "/articles/opds_catalogue.jpeg"); }))
}

@Page("/blog/opds-catalogue-client-kotlin-multiplatform")
@Layout(".pages.MarkdownPage")
@Composable
fun Opds_hostile_inputPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("every-catalogue-is-hostile-an-opds-client-in-kotlin-multiplatform") }) {
        org.jetbrains.compose.web.dom.Text("Every catalogue is hostile: an OPDS client in Kotlin Multiplatform")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("OPDS is how ebook servers publish their catalogues. Project Gutenberg has one, and so do self-hosted servers like Calibre, Kavita and Komga. A client that speaks OPDS can browse any of them, search them and download books. I wanted that in Parrot, so people can fill their library from the servers they already use.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The protocol is simple. The hard part is everything around it: Parrot fetches XML and JSON from servers chosen by the user, follows links those servers choose, sends a password to some of them, and writes files they provide to disk. Each of those is a place where a catalogue can do something you didn't intend. So I designed the client as if every catalogue were hostile, and then had it reviewed as if it were.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-formats-one-model") }) {
        org.jetbrains.compose.web.dom.Text("Two formats, one model")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There are two OPDS versions in the wild. OPDS 1.x is Atom XML and is what almost every server speaks today. OPDS 2 is JSON. Both are parsed in common Kotlin into one model, so the screens never know which one they're showing:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("OPDS 1")
            }
            org.jetbrains.compose.web.dom.Text(" uses xmlutil's streaming reader with a small hand-written walker, rather than a DOM or a serializer, so the parser can count and stop at any point.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("OPDS 2")
            }
            org.jetbrains.compose.web.dom.Text(" uses ")
            io.github.retar.portfolio.components.widgets.InlineCode("""kotlinx.serialization""")
            org.jetbrains.compose.web.dom.Text("'s JSON tree, with a depth check and an item count check before anything is read.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The feed type is decided by media type plus structure, never by \"try one parser, and if it throws, try the other\". An HTML error page, an RSS feed or an authentication document is rejected as what it is, with a matching message, instead of failing somewhere deep inside a parser.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Some plumbing had no maintained Kotlin Multiplatform library, so I wrote it: an RFC 3986 resolver for relative links, an RFC 6570 subset for OpenSearch templates (")
        io.github.retar.portfolio.components.widgets.InlineCode("""{var}""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""{+var}""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""{?list}""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""{&list}""")
        org.jetbrains.compose.web.dom.Text(", and an explicit error for anything else), and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""xml:base""")
        org.jetbrains.compose.web.dom.Text(" handling, because the XML library doesn't do it. One detail mattered more than expected: relative links resolve against the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("final URL after redirects")
        }
        org.jetbrains.compose.web.dom.Text(", not the URL you asked for or the feed's ")
        io.github.retar.portfolio.components.widgets.InlineCode("""self""")
        org.jetbrains.compose.web.dom.Text(" link. Get that wrong and covers and next pages point at the wrong server.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-parser-stops-before-the-entities") }) {
        org.jetbrains.compose.web.dom.Text("The parser stops before the entities")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("xmlutil accepts internal DTDs and doesn't limit entity expansion. That's the classic \"billion laughs\" setup: a few hundred bytes of XML that expand to gigabytes in memory. No OPDS feed needs a DTD, so the parser refuses any document that declares one, before a single entity can be read:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""when (val typeName = reader.next().name) {
    "DOCDECL", "DTD" -> {
        // Phase 1 REQUIRED mitigation: stop before any entity
        // text can be read (Phase 0 record).
        throw StructuralAbort(OpdsRejection.DocumentTypeDeclarationRejected())
    }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything else is bounded by one budget object:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Limit")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Value")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Feed or description response")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5 MiB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Nesting depth")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Items per page")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2,000")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Redirects")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("A downloaded book")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("512 MiB")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Pages saved for offline use")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25 MiB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nesting is counted without recursion, so a feed nested 100,000 levels deep gets a clean rejection instead of a stack overflow. That's a test, not a guess.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-password-never-leaves-its-origin") }) {
        org.jetbrains.compose.web.dom.Text("A password never leaves its origin")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot supports HTTP Basic sign-in, and that's a password sent with every request. The rule: credentials go out only if the request's scheme, host and port all match the catalogue's, the scheme is https, and no earlier redirect in the chain has left that origin.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To make that rule enforceable, each catalogue gets its own HTTP client with automatic redirects turned off, no auth plugin, no cookies and no shared default headers:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""/** One isolated client per source, owning its engine. No auth/cookie/logging plugins or shared default headers. */
class KtorOpdsTransport(
    private val engine: HttpClientEngine,
    private val catalogueRoot: String,
    ...
    private val client = HttpClient(engine) {
        followRedirects = false
        expectSuccess = false
        install(HttpTimeout) { requestTimeoutMillis = 30_000; connectTimeoutMillis = 15_000; socketTimeoutMillis = 30_000 }
    }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Redirects are followed by hand, at most five hops, with loop detection. https to http is refused, and so are URLs with a user name or password embedded in them. Because every hop goes through code I wrote, the ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Authorization""")
        org.jetbrains.compose.web.dom.Text(" header is attached per request, and only when that hop passes the origin check. With a library's redirect handling, I'd have had to trust its rules for when to drop the header.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There's a product side effect. A password-protected Calibre server on a home network uses Digest sign-in over plain http. Parrot won't send a password over http, so that setup isn't supported. I'd rather say \"not supported\" than quietly send a password in the clear.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-security-review-found") }) {
        org.jetbrains.compose.web.dom.Text("What the security review found")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When the client worked, I ran a dedicated security pass over it: transport, parser, downloads, images, logs. It found nine real bugs. A few of them are worth knowing about for any client that fetches URLs on someone else's behalf:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("The catalogue could reach your home network.")
            }
            org.jetbrains.compose.web.dom.Text(" A catalogue on the internet could list a cover image at ")
            io.github.retar.portfolio.components.widgets.InlineCode("""http://192.168.1.1/...""")
            org.jetbrains.compose.web.dom.Text(", and Parrot would fetch it. That's server-side request forgery, from a phone. Requests from a public catalogue to local addresses, including through a redirect, are now refused. The known gap: host names aren't resolved first, so a public name that points at a private address isn't caught yet.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A missing host became ")
                io.github.retar.portfolio.components.widgets.InlineCode("""localhost""")
                org.jetbrains.compose.web.dom.Text(".")
            }
            org.jetbrains.compose.web.dom.Text(" An address like ")
            io.github.retar.portfolio.components.widgets.InlineCode("""https:///feed""")
            org.jetbrains.compose.web.dom.Text(" was requested as ")
            io.github.retar.portfolio.components.widgets.InlineCode("""https://localhost/feed""")
            org.jetbrains.compose.web.dom.Text(". It is now refused.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A download could be bigger than it said.")
            }
            org.jetbrains.compose.web.dom.Text(" A server that sent more bytes than its ")
            io.github.retar.portfolio.components.widgets.InlineCode("""Content-Length""")
            org.jetbrains.compose.web.dom.Text(" got the whole file written before the size check failed. Now the stream stops at the declared length and writes none of the excess:")
        }
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""if (count + read > maxBytes) { exceeded = true; break }
// More than was declared is not the file that was offered: stop, write none of it.
if (declared != null && count + read > declared) { overrun = true; break }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Endless paging.")
            }
            org.jetbrains.compose.web.dom.Text(" A feed whose ")
            io.github.retar.portfolio.components.widgets.InlineCode("""next""")
            org.jetbrains.compose.web.dom.Text(" link pointed back at itself loaded forever. Lists now end when the next link loops, and auto-loading stops after three pages in a row with no books.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The others were smaller: relative cover addresses that were never resolved, a stale sign-in status, a preset's search address surviving an edit of its server address, and an exception message that could carry a URL into a log. The transport now logs a fixed set of words and never an address, because for some servers (Kavita puts an API key in the URL path) the address is the secret.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("downloads-are-checked-by-their-bytes") }) {
        org.jetbrains.compose.web.dom.Text("Downloads are checked by their bytes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A downloaded book streams to a staging file in 64 KiB chunks, with free space checked before the download and again every 4 MiB. The file name is a random UUID. Nothing the server sends, not the title, not ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Content-Disposition""")
        org.jetbrains.compose.web.dom.Text(", ever becomes part of a path.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Then the file is identified by its bytes, never by the server's content type or the URL's extension. The EPUB check refuses ZIP entries whose paths escape the book's folder, caps entry count and size, and detects DRM: encrypted entries, or a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""META-INF/encryption.xml""")
        org.jetbrains.compose.web.dom.Text(" that encrypts content, mark the book as protected. Font obfuscation alone still counts as a normal book. Protected books are refused with an explanation, because Parrot can't open them, and an import that fails later is worse than one that says no up front. Links that are clearly not free downloads (buy, borrow, subscribe, sample, LCP or Adobe DRM) are shown as a link to the provider instead of a download button.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-ios-surprise-a-cache-you-didn-t-ask-for") }) {
        org.jetbrains.compose.web.dom.Text("The iOS surprise: a cache you didn't ask for")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On iOS, Ktor uses the Darwin engine, which uses ")
        io.github.retar.portfolio.components.widgets.InlineCode("""NSURLSession""")
        org.jetbrains.compose.web.dom.Text(", which by default uses the shared ")
        io.github.retar.portfolio.components.widgets.InlineCode("""NSURLCache""")
        org.jetbrains.compose.web.dom.Text(". That means feed pages, search queries and whole book files were being written to ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Library/Caches""")
        org.jetbrains.compose.web.dom.Text(" by the operating system, outside every limit above, with nothing in the shared code to show it. The fix is a small ")
        io.github.retar.portfolio.components.widgets.InlineCode("""expect""")
        org.jetbrains.compose.web.dom.Text("/")
        io.github.retar.portfolio.components.widgets.InlineCode("""actual""")
        org.jetbrains.compose.web.dom.Text(" seam that wraps the Darwin engine and switches the cache off last, so no caller configuration can turn it back on:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""private object CatalogueDarwinEngines : HttpClientEngineFactory<DarwinClientEngineConfig> {
    override fun create(block: DarwinClientEngineConfig.() -> Unit): HttpClientEngine =
        Darwin.create {
            block()
            // Appended last, so nothing a caller configured can put the cache back.
            configureSession {
                URLCache = null
                requestCachePolicy = NSURLRequestReloadIgnoringLocalCacheData
            }
        }
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The test for it was committed failing first. If you use Ktor on iOS for anything sensitive, check what ")
        io.github.retar.portfolio.components.widgets.InlineCode("""NSURLCache""")
        org.jetbrains.compose.web.dom.Text(" is doing for you.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("honest-status") }) {
        org.jetbrains.compose.web.dom.Text("Honest status")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Shipping a client for \"any OPDS server\" means being honest about which servers you've actually tried. So far that's Project Gutenberg, on Android and in the iOS Simulator, plus a local test server that can misbehave on purpose. Calibre, Calibre-Web, Kavita, Komga and Standard Ebooks are still untested on a device, and OPDS 2 has only been tested against fixtures. Those runs come before the catalogue browser is called finished.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Gutenberg alone produced two surprises. Its search description advertises an http-only address, which Parrot rightly refuses, so the preset carries an https search address of its own. And it recently started sending ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Vary: Accept-Language""")
        org.jetbrains.compose.web.dom.Text(", which Parrot's offline cache treats as \"don't store\", so Gutenberg pages are no longer saved for offline reading. Real servers will keep doing things the spec didn't plan for. The budgets and the origin rule are what make that survivable.")
    }
}
